import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { useGarden } from '../../application/plants/useGarden';
import { usePlantCatalogue } from '../../application/plants/usePlantCatalogue';
import { findPlantById } from '../../domain/plants/plantCatalogue';
import { selectGardenPlants } from '../../domain/plants/plantGarden';
import AmbientGarden from '../chrome/AmbientGarden';
import { LeafGlyph } from '../chrome/BrandIcons';
import PlantCard from '../plants/PlantCard';

/**
 * The plants you saved (FR-08, SPEC-014).
 *
 * The collection is a list of ids on this device; the cards are today's content
 * packs, resolved against the fetched catalogue (D-1). So this screen has the
 * same three states every plant screen has — loading, failed, ready — and a
 * fourth that is its own: saved nothing yet.
 *
 * A grid rather than shelves. The shelves on the browse screen exist to give the
 * catalogue the shape of its categories (SPEC-011); a garden of a handful of
 * plants has no shape to give, and a page of one-card rows would be worse than
 * the grid it replaced.
 */

function commonNameOf(plant) {
    return plant.prose?.commonName ?? plant.id;
}

export default function GardenScreen() {
    const { t, localeCode } = useLocale();
    const { status, plants } = usePlantCatalogue();
    const { plantIds, toggle, restore, isCloudSynced } = useGarden();
    // The one plant an Undo would put back — see D-4. One, not a stack: a stack
    // of undos is state with no affordance to reach it.
    const [removed, setRemoved] = useState(null);

    const saved = useMemo(() => selectGardenPlants(plants, plantIds), [plants, plantIds]);

    // Every card on this screen is a saved plant, so its heart can only remove.
    const remove = (plantId) => {
        const plant = findPlantById(plants, plantId);

        setRemoved({ plantId, index: plantIds.indexOf(plantId), name: commonNameOf(plant) });
        toggle(plantId);
    };

    const undo = () => {
        restore(removed.plantId, removed.index);
        setRemoved(null);
    };

    return (
        <section className="garden-screen">
            {/* The ground this screen stands in (SPEC-023). First child so the
                document order matches the paint order; it is `position: fixed`
                and takes no space. */}
            <AmbientGarden />
            <h1 className="screen__title">{t('garden.title')}</h1>

            {status === 'ready' && saved.length > 0 ? (
                <p className="garden-screen__summary">
                    {t('garden.count', { count: saved.length })}
                    {/* The limit, stated rather than implied away (SPEC-014 D-5) —
                        and no longer implied when it stopped being true: signed
                        in, the list follows the account (SPEC-022). */}
                    <span className="garden-screen__note">
                        {isCloudSynced
                            ? t('garden.accountNote')
                            : `${t('garden.deviceNote')} ${t('garden.signInHint')}`}
                    </span>
                </p>
            ) : null}

            {/* The removed card is simply gone, so this line is the only
                announcement a screen reader gets that anything happened — and the
                only way back, since the heart that would undo it left with it. */}
            <p className="garden-screen__undo" role="status">
                {removed ? (
                    <>
                        {t('garden.removed', { plant: removed.name })}
                        <button type="button" className="button button--quiet" onClick={undo}>
                            {t('garden.undo')}
                        </button>
                    </>
                ) : null}
            </p>

            {status === 'loading' ? <p className="plants-screen__status">{t('plants.loading')}</p> : null}

            {status === 'failed' ? (
                <div className="empty-state">
                    <LeafGlyph className="empty-state__mark" size={32} />
                    <p className="empty-state__message">{t('plants.failed')}</p>
                </div>
            ) : null}

            {status === 'ready' && saved.length === 0 ? (
                <div className="empty-state">
                    <LeafGlyph className="empty-state__mark" size={32} />
                    <p className="empty-state__message">{t('garden.empty')}</p>
                    <Link className="button button--primary" to={`/${localeCode}/plants`}>
                        {t('garden.browse')}
                    </Link>
                </div>
            ) : null}

            {saved.length > 0 ? (
                <ul className="garden-grid">
                    {saved.map((plant) => (
                        <li className="garden-grid__item" key={plant.id}>
                            <PlantCard plant={plant} isSaved onToggleSave={remove} />
                        </li>
                    ))}
                </ul>
            ) : null}
        </section>
    );
}
